import { SearchableSelect } from "@/components/ui/searchable-select";
import { dateLocale } from "@/lib/dateTime";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StageMoveDialog, type StageMoveRequest } from "@/components/deals/StageMoveDialog";
import { StatusPill } from "@/components/patterns";
import { ArrowRight, CheckCircle2, Circle, FileCheck2, Flag, Loader2, Paperclip, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";

type GateItem = { key: string; label: string; met: boolean; hint?: string };
type Milestone = { id: string; key: string; label: string; source: string; note: string | null; file_name: string | null; has_file: boolean; created_on: string };
type Qualification = {
  budget_amount: string | number | null;
  budget_confirmed: boolean;
  decision_maker_contact_id: string | null;
  authority_notes: string | null;
  need: string | null;
  timeline_date: string | null;
  timeline_notes: string | null;
  competitors: string | null;
  pain_points: string | null;
};
type Gates = {
  stage: { id: string; name: string; key: string | null; sla_days: number | null } | null;
  next_stage: { id: string; name: string; key: string } | null;
  gated: boolean;
  checklist: GateItem[];
  ready: boolean;
  days_in_stage: number;
  over_time_limit: boolean;
  qualification: Qualification | null;
  milestones: Milestone[];
};
type ContactOption = { id: string; first_name: string; last_name?: string; job_title?: string };

const EMPTY_BANT = { budget_amount: "", budget_confirmed: false, decision_maker_contact_id: "", authority_notes: "", need: "", timeline_date: "", timeline_notes: "", competitors: "", pain_points: "" };
type Bant = typeof EMPTY_BANT;

interface Ctx {
  gates: Gates | null;
  isClosed: boolean;
  bant: Bant;
  setBant: (b: Bant) => void;
  contacts: ContactOption[];
  saving: boolean;
  saveBant: (override?: Partial<Bant>) => Promise<void>;
  askMove: () => void;
  openRecord: (kind: "PROPOSAL_ACCEPTED" | "SIGNED") => void;
  downloadMilestoneFile: (m: Milestone) => void;
}
const GatesContext = createContext<Ctx | null>(null);
export function useDealGates() {
  const c = useContext(GatesContext);
  if (!c) throw new Error("useDealGates must be used inside <DealGatesProvider>");
  return c;
}

const field = "w-full h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-primary";

/**
 * What the deal's stage needs before it can move on, and the qualification (BANT) answers. Both the
 * "To move on" card and the Qualification tab read it from here, so they always agree.
 */
export function DealGatesProvider({
  dealId,
  dealName,
  accountId,
  isClosed,
  onChanged,
  children,
}: {
  dealId: string;
  dealName: string;
  accountId?: string;
  isClosed: boolean;
  onChanged: () => void;
  children: React.ReactNode;
}) {
  const { success, error } = useToast();
  const [gates, setGates] = useState<Gates | null>(null);
  const [bant, setBant] = useState<Bant>(EMPTY_BANT);
  const [contacts, setContacts] = useState<ContactOption[]>([]);
  const [saving, setSaving] = useState(false);
  const [moveRequest, setMoveRequest] = useState<StageMoveRequest>(null);
  const [recordOpen, setRecordOpen] = useState<null | "PROPOSAL_ACCEPTED" | "SIGNED">(null);
  const [recordKind, setRecordKind] = useState<"AGREEMENT_SIGNED" | "PO_RECEIVED">("AGREEMENT_SIGNED");
  const [recordNote, setRecordNote] = useState("");
  const [recordFile, setRecordFile] = useState<File | null>(null);

  const bantFrom = (q: Qualification | null | undefined): Bant => ({
    budget_amount: q?.budget_amount != null ? String(Number(q.budget_amount)) : "",
    budget_confirmed: !!q?.budget_confirmed,
    decision_maker_contact_id: q?.decision_maker_contact_id || "",
    authority_notes: q?.authority_notes || "",
    need: q?.need || "",
    timeline_date: q?.timeline_date ? String(q.timeline_date).slice(0, 10) : "",
    timeline_notes: q?.timeline_notes || "",
    competitors: q?.competitors || "",
    pain_points: q?.pain_points || "",
  });

  const load = useCallback(async () => {
    try {
      const g: any = await api.get(`/api/Deal/Gates/${dealId}`);
      setGates(g);
      setBant(bantFrom(g?.qualification));
    } catch (err: any) {
      error("Could not load the stage requirements", err.message || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!accountId) return;
    api
      .get(`/api/Contact/List?account_id=${accountId}&page_size=100`)
      .then((res) => setContacts(unwrapPage<any>(res, 100).rows))
      .catch(() => setContacts([]));
  }, [accountId]);

  const saveBant = async (override: Partial<Bant> = {}) => {
    const b = { ...bant, ...override };
    setSaving(true);
    try {
      const body = {
        budget_amount: b.budget_amount === "" ? null : Number(b.budget_amount),
        budget_confirmed: b.budget_confirmed,
        decision_maker_contact_id: b.decision_maker_contact_id || null,
        authority_notes: b.authority_notes || null,
        need: b.need || null,
        timeline_date: b.timeline_date || null,
        timeline_notes: b.timeline_notes || null,
        competitors: b.competitors || null,
        pain_points: b.pain_points || null,
      };
      const g: any = await api.put(`/api/Deal/Qualification/${dealId}`, body);
      setGates(g);
      setBant(bantFrom(g?.qualification));
      success("Qualification saved", g?.ready ? "Ready to move to the next stage" : "");
      onChanged();
    } catch (err: any) {
      error("Could not save", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  const recordMilestone = async () => {
    if (!recordOpen) return;
    setSaving(true);
    try {
      let file_url: string | undefined;
      let file_name: string | undefined;
      if (recordFile) {
        const fd = new FormData();
        fd.append("deal_id", dealId);
        fd.append("file", recordFile);
        const up: any = await api.upload("/api/Upload/File", fd);
        file_url = up?.file_url;
        file_name = recordFile.name;
      }
      const res: any = await api.post(`/api/Deal/Milestone/${dealId}`, {
        milestone_key: recordOpen === "PROPOSAL_ACCEPTED" ? "PROPOSAL_ACCEPTED" : recordKind,
        note: recordNote.trim(),
        file_url,
        file_name,
      });
      success("Recorded", res?.advanced_to ? `The deal moved to ${res.advanced_to}` : "Milestone saved");
      setRecordOpen(null);
      setRecordNote("");
      setRecordFile(null);
      await load();
      onChanged();
    } catch (err: any) {
      error("Could not record it", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };

  const downloadMilestoneFile = async (m: Milestone) => {
    try {
      const res = await fetch(`/api/Deal/MilestoneFile/${m.id}`, { credentials: "include" });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err: any) {
      error("Could not open the file", err.message || "");
    }
  };

  const value = useMemo<Ctx>(
    () => ({
      gates,
      isClosed,
      bant,
      setBant,
      contacts,
      saving,
      saveBant,
      askMove: () => gates?.next_stage && setMoveRequest({ dealId, dealName, stage: { id: gates.next_stage.id, name: gates.next_stage.name } }),
      openRecord: setRecordOpen,
      downloadMilestoneFile,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gates, isClosed, bant, contacts, saving, dealId, dealName],
  );

  return (
    <GatesContext.Provider value={value}>
      {children}

      <StageMoveDialog
        request={moveRequest}
        onClose={() => setMoveRequest(null)}
        onMoved={() => {
          load();
          onChanged();
        }}
      />

      <Dialog open={!!recordOpen} onOpenChange={(o) => !o && !saving && setRecordOpen(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{recordOpen === "PROPOSAL_ACCEPTED" ? "Customer accepted the proposal" : "Signed agreement or purchase order"}</DialogTitle>
            <DialogDescription>
              {recordOpen === "PROPOSAL_ACCEPTED"
                ? "For an acceptance outside the CRM (email, call, meeting). The deal then moves to Quotation."
                : "Attach the signed copy or the customer's PO. The deal is then closed as Won."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {recordOpen === "SIGNED" && (
              <div className="space-y-1">
                <Label className="text-xs">What did you receive?</Label>
                <SearchableSelect value={recordKind} onChange={(e) => setRecordKind(e.target.value as any)} className={field}>
                  <option value="AGREEMENT_SIGNED">Signed agreement / contract</option>
                  <option value="PO_RECEIVED">Purchase order (PO)</option>
                </SearchableSelect>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-xs">How and when did the customer confirm? *</Label>
              <Textarea rows={3} value={recordNote} onChange={(e) => setRecordNote(e.target.value)} placeholder="e.g. IT head approved by email on 8 Oct" />
            </div>
            {recordOpen === "SIGNED" && (
              <div className="space-y-1">
                <Label className="text-xs">File *</Label>
                <Input type="file" accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" onChange={(e) => setRecordFile(e.target.files?.[0] || null)} />
              </div>
            )}
            {recordOpen === "SIGNED" && <p className="text-[11px] text-muted-foreground">Closing as Won uses the deal amount (set from the accepted quotation).</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRecordOpen(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={recordMilestone} disabled={saving || !recordNote.trim() || (recordOpen === "SIGNED" && !recordFile)}>
              {saving ? <Loader2 className="size-4 animate-spin mr-1.5" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </GatesContext.Provider>
  );
}

const CardShell = ({ title, aside, children }: { title: string; aside?: React.ReactNode; children: React.ReactNode }) => (
  <section className="flex flex-col gap-2.5 rounded-xl border border-border bg-card px-4 py-3.5">
    <div className="flex items-center justify-between gap-2">
      <h2 className="text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">{title}</h2>
      {aside}
    </div>
    {children}
  </section>
);

/** Side card: what this stage still needs, how long the deal has been in it, and the way to move on */
export function DealMoveCard() {
  const { gates, isClosed, askMove, openRecord, downloadMilestoneFile } = useDealGates();
  if (!gates || !gates.gated) return null;
  const key = gates.stage?.key;
  const done = gates.checklist.filter((i) => i.met).length;
  const limit = gates.stage?.sla_days || 0;
  const pct = limit ? Math.min(100, Math.round((gates.days_in_stage / limit) * 100)) : 0;
  const canMove = !isClosed && gates.next_stage && key !== "AGREEMENT";

  return (
    <CardShell
      title={isClosed ? "Where it ended" : gates.next_stage ? `To move to ${gates.next_stage.name}` : `In ${gates.stage?.name}`}
      aside={!isClosed && gates.checklist.length > 0 ? <StatusPill tone={gates.ready ? "success" : "warning"}>{gates.ready ? "Ready" : `${done} of ${gates.checklist.length} done`}</StatusPill> : undefined}
    >
      {!isClosed && gates.checklist.length > 0 && (
        <ul className="flex flex-col gap-2">
          {gates.checklist.map((i) => (
            <li key={i.key} className="flex items-start gap-2 text-[13px]">
              {i.met ? <CheckCircle2 className="mt-0.5 size-[18px] shrink-0 text-success" /> : <Circle className="mt-0.5 size-[18px] shrink-0 text-muted-foreground/60" />}
              <span className="min-w-0">
                <span className={i.met ? "text-muted-foreground line-through" : "font-semibold text-foreground"}>{i.label}</span>
                {!i.met && i.hint && <span className="block text-xs text-muted-foreground">{i.hint}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}

      {!isClosed && (
        <div className="flex flex-col gap-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>
              {gates.days_in_stage} {gates.days_in_stage === 1 ? "day" : "days"} in {gates.stage?.name}
              {limit ? ` of ${limit}` : ""}
            </span>
            {limit > 0 && <span className={gates.over_time_limit ? "font-medium text-danger" : ""}>{gates.over_time_limit ? "Over the limit" : "On time"}</span>}
          </div>
          {limit > 0 && (
            <div className="h-1.5 rounded-sm bg-muted">
              <div className={cn("h-full rounded-sm", gates.over_time_limit ? "bg-danger" : "bg-success")} style={{ width: `${pct}%` }} />
            </div>
          )}
        </div>
      )}

      {!isClosed && (
        <div className="flex flex-wrap gap-2">
          {key === "PROPOSAL" && !gates.ready && (
            <Button size="sm" variant="outline" className="h-8 flex-1 gap-1 text-xs" onClick={() => openRecord("PROPOSAL_ACCEPTED")}>
              <Flag className="size-3.5" /> Record acceptance
            </Button>
          )}
          {key === "AGREEMENT" && !gates.ready && (
            <Button size="sm" className="h-8 flex-1 gap-1 text-xs" onClick={() => openRecord("SIGNED")}>
              <FileCheck2 className="size-3.5" /> Upload signed copy / PO
            </Button>
          )}
          {canMove && (
            <Button size="sm" variant={gates.ready ? "default" : "outline"} className="h-8 flex-1 gap-1 text-xs" onClick={askMove}>
              Move to {gates.next_stage!.name} <ArrowRight className="size-3.5" />
            </Button>
          )}
        </div>
      )}
      {!isClosed && key === "QUOTATION" && !gates.ready && <p className="text-xs text-muted-foreground">When the customer accepts, mark the quotation as accepted under Products &amp; quote; the deal then moves to Agreement.</p>}

      {gates.milestones.length > 0 && (
        <div className="flex flex-col gap-1.5 border-t border-border pt-2.5">
          <p className="text-xs font-semibold">Milestones</p>
          {gates.milestones.map((m) => (
            <div key={m.id} className="flex items-start gap-2 text-xs">
              <CheckCircle2 className="size-4 shrink-0 text-success" />
              <div className="min-w-0">
                <p className="text-foreground">
                  {m.label}
                  <span className="text-muted-foreground">
                    {" "}
                    · {new Date(m.created_on).toLocaleDateString(dateLocale())}
                    {m.source === "MANUAL" ? " · recorded by hand" : ""}
                  </span>
                </p>
                {m.note && <p className="break-words text-muted-foreground">{m.note}</p>}
                {m.has_file && (
                  <button type="button" onClick={() => downloadMilestoneFile(m)} className="inline-flex items-center gap-1 text-primary hover:underline">
                    <Paperclip className="size-3" /> {m.file_name || "Attachment"}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </CardShell>
  );
}

/** How many of the four BANT answers are in (budget, decision maker, need of 10+ characters, timeline) */
export function qualificationKnown(q: { budget_amount: string | number | null; decision_maker_contact_id: string | null; need: string | null; timeline_date: string | null } | null | undefined) {
  return {
    budget: Number(q?.budget_amount || 0) > 0,
    authority: !!q?.decision_maker_contact_id,
    need: (q?.need || "").trim().length >= 10,
    timeline: !!q?.timeline_date,
  };
}

const fmtDate = (d?: string | null) => (d ? new Date(`${String(d).slice(0, 10)}T00:00:00`).toLocaleDateString(dateLocale(), { day: "numeric", month: "short", year: "numeric" }) : "");
const money = (n: string | number | null | undefined) => (n == null || n === "" ? "" : `₹${Number(n).toLocaleString("en-IN")}`);

function BantCard({ letter, title, known, value, note, children, onEdit }: { letter: string; title: string; known: boolean; value: React.ReactNode; note?: React.ReactNode; children?: React.ReactNode; onEdit?: () => void }) {
  return (
    <div className={cn("flex flex-col gap-1.5 rounded-[10px] bg-card px-3.5 py-3", known ? "border border-border" : "border-[1.5px] border-warning")}>
      <div className="flex items-center gap-2">
        <span className={cn("flex size-[26px] items-center justify-center rounded-[7px] text-[13px] font-bold", known ? "bg-success-soft text-success" : "bg-warning-soft text-warning")}>{letter}</span>
        <span className="flex-1 font-semibold">{title}</span>
        <StatusPill tone={known ? "success" : "warning"}>{known ? "Known" : "Missing"}</StatusPill>
        {onEdit && (
          <button type="button" onClick={onEdit} aria-label={`Edit ${title}`} className="text-muted-foreground hover:text-primary">
            <Pencil className="size-3.5" />
          </button>
        )}
      </div>
      <div className={cn("text-sm", known ? "font-medium text-foreground" : "text-muted-foreground")}>{value}</div>
      {note && <div className="text-xs text-muted-foreground">{note}</div>}
      {children}
    </div>
  );
}

const chips = (text?: string | null) =>
  (text || "")
    .split(/[,;\n]/)
    .map((s) => s.trim())
    .filter(Boolean);

/** The four BANT answers as cards, with the missing ones called out and a form to change any of them */
export function DealQualificationTab() {
  const { gates, isClosed, bant, setBant, contacts, saving, saveBant } = useDealGates();
  const [editing, setEditing] = useState(false);
  const [quickDate, setQuickDate] = useState("");
  if (!gates) return <p className="py-6 text-center text-sm text-muted-foreground">Loading…</p>;
  const q = gates.qualification;
  const dm = contacts.find((c) => c.id === q?.decision_maker_contact_id);
  const dmName = dm ? [dm.first_name, dm.last_name].filter(Boolean).join(" ") : "";
  const known = qualificationKnown(q);
  const count = Object.values(known).filter(Boolean).length;
  const canEdit = !isClosed;

  if (editing && canEdit) {
    return (
      <div className="flex flex-col gap-3">
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
          <div className="space-y-1">
            <Label className="text-xs">Budget (₹)</Label>
            <Input type="number" min="0" value={bant.budget_amount} onChange={(e) => setBant({ ...bant, budget_amount: e.target.value })} className="h-9" />
            <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <input type="checkbox" checked={bant.budget_confirmed} onChange={(e) => setBant({ ...bant, budget_confirmed: e.target.checked })} />
              Confirmed by the customer
            </label>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Decision maker</Label>
            <SearchableSelect value={bant.decision_maker_contact_id} onChange={(e) => setBant({ ...bant, decision_maker_contact_id: e.target.value })} className={field}>
              <option value="">— Choose a contact —</option>
              {contacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {[c.first_name, c.last_name].filter(Boolean).join(" ")}
                  {c.job_title ? ` · ${c.job_title}` : ""}
                </option>
              ))}
            </SearchableSelect>
            {contacts.length === 0 && <p className="text-[11px] text-muted-foreground">Add the decision maker as a contact of this account first.</p>}
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs">Decision maker notes</Label>
            <Input value={bant.authority_notes} onChange={(e) => setBant({ ...bant, authority_notes: e.target.value })} className="h-9" placeholder="Who signs off, who places the order" />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label className="text-xs">Need: what problem are they solving?</Label>
            <Textarea rows={2} value={bant.need} onChange={(e) => setBant({ ...bant, need: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Timeline: when do they want to buy / go live?</Label>
            <Input type="date" value={bant.timeline_date} onChange={(e) => setBant({ ...bant, timeline_date: e.target.value })} className="h-9" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Timeline notes</Label>
            <Input value={bant.timeline_notes} onChange={(e) => setBant({ ...bant, timeline_notes: e.target.value })} className="h-9" placeholder="e.g. Before the new financial year" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Competitors (optional, separate with commas)</Label>
            <Input value={bant.competitors} onChange={(e) => setBant({ ...bant, competitors: e.target.value })} className="h-9" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Pain points (optional, separate with commas)</Label>
            <Input value={bant.pain_points} onChange={(e) => setBant({ ...bant, pain_points: e.target.value })} className="h-9" />
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={saving}
            onClick={async () => {
              await saveBant();
              setEditing(false);
            }}
          >
            {saving ? <Loader2 className="mr-1 size-3.5 animate-spin" /> : null}
            Save qualification
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span>
          <strong className="text-[15px]">Qualification</strong>
          <span className="block text-[12.5px] text-muted-foreground">Budget, authority, need and timeline: why this deal is real.</span>
        </span>
        <span className="flex items-center gap-2">
          <StatusPill tone={count === 4 ? "success" : "warning"}>{count} of 4 known</StatusPill>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
              <Pencil className="mr-1.5 size-3.5" />
              Edit
            </Button>
          )}
        </span>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <BantCard
          letter="B"
          title="Budget"
          known={known.budget}
          value={known.budget ? `${money(q?.budget_amount)}${q?.budget_confirmed ? " · confirmed by the customer" : " · not yet confirmed"}` : "No budget entered"}
          note={known.budget ? undefined : "Needed before Quotation."}
          onEdit={canEdit ? () => setEditing(true) : undefined}
        />
        <BantCard
          letter="A"
          title="Authority"
          known={known.authority}
          value={known.authority ? dmName || "Decision maker chosen" : "Decision maker not chosen"}
          note={known.authority ? q?.authority_notes || undefined : "Choose the contact who decides."}
          onEdit={canEdit ? () => setEditing(true) : undefined}
        />
        <BantCard letter="N" title="Need" known={known.need} value={q?.need?.trim() || "Need not described"} note={known.need ? undefined : "Describe it in at least 10 characters."} onEdit={canEdit ? () => setEditing(true) : undefined} />
        <BantCard
          letter="T"
          title="Timeline"
          known={known.timeline}
          value={known.timeline ? fmtDate(q?.timeline_date) : "Not set yet"}
          note={known.timeline ? q?.timeline_notes || undefined : "Needed before Quotation."}
          onEdit={canEdit ? () => setEditing(true) : undefined}
        >
          {!known.timeline && canEdit && (
            <div className="mt-1 flex items-center gap-2">
              <Input type="date" value={quickDate} onChange={(e) => setQuickDate(e.target.value)} className="h-8 w-40" aria-label="Timeline date" />
              <Button size="sm" disabled={!quickDate || saving} onClick={() => saveBant({ timeline_date: quickDate })}>
                Set timeline
              </Button>
            </div>
          )}
        </BantCard>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {[
          ["Competitors", chips(q?.competitors)],
          ["Pain points", chips(q?.pain_points)],
        ].map(([label, list]) => (
          <div key={label as string} className="rounded-[10px] border border-border bg-card px-3.5 py-3">
            <div className="mb-1.5 font-semibold">{label as string}</div>
            <div className="flex flex-wrap items-center gap-1.5">
              {(list as string[]).length === 0 ? <span className="text-xs text-muted-foreground">None noted.</span> : (list as string[]).map((c) => <StatusPill key={c} tone="neutral" dot={false}>{c}</StatusPill>)}
              {canEdit && (
                <button type="button" onClick={() => setEditing(true)} className="text-xs text-primary hover:underline">
                  + Add
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
